import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Plus, RotateCcw, X } from "lucide-react";
import {
  type InviteLink,
  type OwnChat,
  type Person,
  refOf,
  settle,
  useAnswerJoinRequests,
  useChatEntrance,
  useCreateInviteLink,
  useRenewMainLink,
  useRevokeInviteLink,
} from "@/lib/ownChats";
import { formatRelative } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Live, PersonLine, RereadButton } from "./OwnChatPeople";

const LIST = "divide-y divide-border rounded-xl border border-border bg-bg-primary";
const ICON_BUTTON =
  "shrink-0 rounded-md p-1.5 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50";

export function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={t("own_chats.copy")}
      title={t("own_chats.copy")}
      className={ICON_BUTTON}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          pushToast({ kind: "success", text: t("own_chats.copied") });
        } catch {
          // The browser may refuse the clipboard: the text is on screen to select.
          pushToast({ kind: "error", text: t("own_chats.copy_failed") });
        }
      }}
    >
      <Copy size={15} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

/**
 * The ways into the chat — its main link and the ones made for an occasion —
 * and the people waiting behind a link that asks an admin first. Both are read
 * from Telegram together: one connection, not two.
 */
export function Links({ chat, active }: { chat: OwnChat; active: boolean }) {
  const { t } = useTranslation();
  const entrance = useChatEntrance(chat.id, active);
  const renew = useRenewMainLink();
  const revoke = useRevokeInviteLink();
  const [renewing, setRenewing] = useState(false);
  const [revoking, setRevoking] = useState<InviteLink | null>(null);
  const [adding, setAdding] = useState(false);
  // Issuing the main link stops the one before it. Whether there is one is
  // what Telegram lists, when that has been read; the chat's row may be behind.
  const hasMain = entrance.data
    ? entrance.data.links.some((l) => l.is_main)
    : chat.invite_link !== null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-[10rem] flex-1 text-sm font-semibold text-fg-primary">
          {t("own_chats.links.title")}
        </h3>
        <RereadButton query={entrance} />
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAdding((v) => !v)}
          iconLeft={<Plus size={15} strokeWidth={2.5} aria-hidden="true" />}
        >
          {t("own_chats.links.add")}
        </Button>
      </div>

      {adding && <NewLink chat={chat} onDone={() => setAdding(false)} />}

      <Live query={entrance}>
        {({ links, requests }) => (
          <div className="space-y-6">
            {links.length === 0 ? (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border px-4 py-5">
                <p className="min-w-0 flex-1 text-sm text-fg-muted">{t("own_chats.links.none")}</p>
                <Button size="sm" onClick={() => setRenewing(true)}>
                  {t("own_chats.links.issue_main")}
                </Button>
              </div>
            ) : (
              <ul className={LIST}>
                {links.map((link) => (
                  <li key={link.link} className="flex items-center gap-2 px-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate font-mono text-sm text-fg-primary">
                          {link.link}
                        </span>
                        {link.is_main && <Badge tone="accent">{t("own_chats.links.main")}</Badge>}
                        {link.request_needed && (
                          <Badge tone="info">{t("own_chats.links.by_request")}</Badge>
                        )}
                      </span>
                      <span className="block truncate text-xs text-fg-muted">
                        {[
                          link.title,
                          link.usage_limit !== null
                            ? t("own_chats.links.joined_of", {
                                joined: link.joined,
                                limit: link.usage_limit,
                              })
                            : t("own_chats.links.joined", { count: link.joined }),
                          link.requests_pending > 0
                            ? t("own_chats.links.waiting", { count: link.requests_pending })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    <CopyButton text={link.link} />
                    {link.is_main ? (
                      <button
                        type="button"
                        aria-label={t("own_chats.links.renew")}
                        title={t("own_chats.links.renew")}
                        className={ICON_BUTTON}
                        onClick={() => setRenewing(true)}
                      >
                        <RotateCcw size={15} strokeWidth={2} aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-label={t("own_chats.links.revoke")}
                        title={t("own_chats.links.revoke")}
                        className={`${ICON_BUTTON} hover:text-danger`}
                        onClick={() => setRevoking(link)}
                      >
                        <X size={15} strokeWidth={2} aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <Requests chat={chat} people={requests} />
          </div>
        )}
      </Live>

      <ConfirmDialog
        open={renewing}
        onOpenChange={setRenewing}
        tone={hasMain ? "danger" : "default"}
        title={t(hasMain ? "own_chats.links.renew_confirm" : "own_chats.links.issue_main")}
        description={t(
          hasMain ? "own_chats.links.renew_confirm_hint" : "own_chats.links.issue_hint",
        )}
        confirmText={t(hasMain ? "own_chats.links.renew" : "own_chats.links.issue_main")}
        cancelText={t("common.cancel")}
        onConfirm={() => settle(renew, { id: chat.id })}
      />
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(o) => !o && setRevoking(null)}
        tone="danger"
        title={t("own_chats.links.revoke_confirm")}
        description={t("own_chats.links.revoke_confirm_hint")}
        confirmText={t("own_chats.links.revoke")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (revoking) await settle(revoke, { id: chat.id, link: revoking.link });
        }}
      />
    </div>
  );
}

function NewLink({ chat, onDone }: { chat: OwnChat; onDone: () => void }) {
  const { t } = useTranslation();
  const create = useCreateInviteLink();
  const [title, setTitle] = useState("");
  const [byRequest, setByRequest] = useState(false);
  const [limit, setLimit] = useState("");
  const cap = Number.parseInt(limit, 10);

  return (
    <div className="space-y-3 rounded-xl border border-border bg-bg-primary p-4">
      <div className="space-y-1.5">
        <Label htmlFor="oc_link_title">{t("own_chats.links.field_title")}</Label>
        <Input
          id="oc_link_title"
          value={title}
          maxLength={32}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("own_chats.links.field_title_placeholder") ?? ""}
        />
      </div>
      <Switch
        checked={byRequest}
        onCheckedChange={setByRequest}
        label={t("own_chats.links.field_request")}
        description={t("own_chats.links.field_request_hint")}
      />
      {!byRequest && (
        <div className="space-y-1.5">
          <Label htmlFor="oc_link_limit">{t("own_chats.links.field_limit")}</Label>
          <Input
            id="oc_link_limit"
            value={limit}
            inputMode="numeric"
            onChange={(e) => setLimit(e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder={t("own_chats.links.field_limit_placeholder") ?? ""}
            className="max-w-[12rem]"
          />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          loading={create.isPending}
          onClick={() =>
            create.mutate(
              {
                id: chat.id,
                title: title.trim(),
                request_needed: byRequest,
                usage_limit: !byRequest && cap > 0 ? cap : null,
              },
              { onSuccess: onDone },
            )
          }
        >
          {t("own_chats.links.create")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        {create.isPending && <span className="text-xs text-fg-muted">{t("own_chats.working")}</span>}
      </div>
    </div>
  );
}

function Requests({ chat, people }: { chat: OwnChat; people: Person[] }) {
  const { t } = useTranslation();
  const answer = useAnswerJoinRequests();
  const [all, setAll] = useState<"approve" | "decline" | null>(null);
  const busyWith = answer.isPending ? answer.variables?.user?.tg_user_id : undefined;

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-fg-primary">{t("own_chats.requests.title")}</h3>
      {people.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-fg-muted">
          {t("own_chats.requests.empty")}
        </p>
      ) : (
        <>
          <ul className={LIST}>
            {people.map((person) => {
              const user = refOf(person);
              return (
                <PersonLine
                  key={person.tg_user_id}
                  person={person}
                  note={[person.since ? formatRelative(person.since) : null, person.about]
                    .filter(Boolean)
                    .join(" · ")}
                  actions={
                    user && (
                      <span className="flex shrink-0 items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={answer.isPending}
                          loading={busyWith === person.tg_user_id && answer.variables?.approve}
                          onClick={() => answer.mutate({ id: chat.id, user, approve: true })}
                          iconLeft={<Check size={14} strokeWidth={2.5} aria-hidden="true" />}
                        >
                          {t("own_chats.requests.approve")}
                        </Button>
                        <button
                          type="button"
                          disabled={answer.isPending}
                          aria-label={t("own_chats.requests.decline")}
                          title={t("own_chats.requests.decline")}
                          className={`${ICON_BUTTON} hover:text-danger`}
                          onClick={() => answer.mutate({ id: chat.id, user, approve: false })}
                        >
                          <X size={15} strokeWidth={2} aria-hidden="true" />
                        </button>
                      </span>
                    )
                  }
                />
              );
            })}
          </ul>
          {people.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setAll("approve")}>
                {t("own_chats.requests.approve_all", { count: people.length })}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAll("decline")}>
                {t("own_chats.requests.decline_all")}
              </Button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={all !== null}
        onOpenChange={(o) => !o && setAll(null)}
        tone={all === "decline" ? "danger" : "default"}
        title={t(`own_chats.requests.${all ?? "approve"}_all_confirm`)}
        confirmText={t(
          all === "decline" ? "own_chats.requests.decline_all" : "own_chats.requests.approve_all_short",
        )}
        cancelText={t("common.cancel")}
        onConfirm={() => settle(answer, { id: chat.id, approve: all === "approve" })}
      />
    </section>
  );
}
